import React, { useState, useEffect, useReducer, useContext } from "react";
import { toast } from "react-toastify";

import { useHistory } from "react-router-dom";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import IconButton from "@material-ui/core/IconButton";
import SearchIcon from "@material-ui/icons/Search";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";

import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import PeopleIcon from "@material-ui/icons/People";
import DownloadIcon from "@material-ui/icons/GetApp";
import AddIcon from "@material-ui/icons/Add";
import SettingsIcon from "@material-ui/icons/Settings";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import GuideOpenButton from "../../components/GuideOpenButton";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import ContactListDialog from "../../components/ContactListDialog";
import ConfirmationModal from "../../components/ConfirmationModal";
import ColumnAliasModal from "../../components/ColumnAliasModal";
import toastError from "../../errors/toastError";
import { Grid, Dialog, DialogTitle, DialogContent, DialogActions, Typography, useTheme } from "@material-ui/core";

import planilhaExemplo from "../../assets/planilha.xlsx";
import { SocketContext } from "../../context/Socket/SocketContext";
import {
  useViewMode,
  ViewModeToggle,
  RecordsCardsGrid,
  RecordCard,
  RecordCardMeta,
} from "../../components/ViewModeToggle";

const reducer = (state, action) => {
  if (action.type === "LOAD_CONTACTLISTS") {
    const contactLists = action.payload;
    const newContactLists = [];

    contactLists.forEach((contactList) => {
      const contactListIndex = state.findIndex((u) => u.id === contactList.id);
      if (contactListIndex !== -1) {
        state[contactListIndex] = contactList;
      } else {
        newContactLists.push(contactList);
      }
    });

    return [...state, ...newContactLists];
  }

  if (action.type === "UPDATE_CONTACTLIST") {
    const contactList = action.payload;
    const contactListIndex = state.findIndex((u) => u.id === contactList.id);

    if (contactListIndex !== -1) {
      state[contactListIndex] = contactList;
      return [...state];
    } else {
      return [contactList, ...state];
    }
  }

  if (action.type === "DELETE_CONTACTLIST") {
    const contactListId = action.payload;

    const contactListIndex = state.findIndex((u) => u.id === contactListId);
    if (contactListIndex !== -1) {
      state.splice(contactListIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const useStyles = makeStyles((theme) => ({
  // ✅ ESTILOS PRINCIPALES MEJORADOS CON TEMA DARK
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: "scroll",
    backgroundColor: theme.mode === 'dark' ? '#1f2937' : theme.palette.background.paper,
    borderRadius: 12,
    border: `1px solid ${theme.mode === 'dark' ? '#4B5563' : '#e0e0e0'}`,
    boxShadow: theme.mode === 'dark' ? '0 4px 20px rgba(0,0,0,0.3)' : '0 2px 8px rgba(0,0,0,0.1)',
    ...theme.scrollbarStyles,
  },
  
  // ✅ ESTILOS DE TABLA MEJORADOS CON TEMA DARK
  tableHeader: {
    backgroundColor: theme.mode === 'dark' ? '#374151' : '#1976d2',
    color: 'white',
    fontWeight: 'bold',
    fontSize: '0.875rem',
    padding: theme.spacing(1.5),
    borderBottom: `2px solid ${theme.mode === 'dark' ? '#4B5563' : '#1565c0'}`,
  },
  
  // ✅ ESTILOS DE FILAS DE TABLA CON TEMA DARK
  tableRow: {
    backgroundColor: theme.mode === 'dark' ? '#1f2937' : theme.palette.background.paper,
    '&:hover': {
      backgroundColor: theme.mode === 'dark' ? '#374151' : '#f5f5f5',
      transition: 'background-color 0.2s ease',
    },
    borderBottom: `1px solid ${theme.mode === 'dark' ? '#374151' : '#e0e0e0'}`,
  },
  
  tableCell: {
    color: theme.palette.text.primary,
    padding: theme.spacing(1.5),
  },
  
  // ✅ ESTILOS DE BOTONES MEJORADOS CON TEMA DARK
  primaryButton: {
    backgroundColor: '#1976d2',
    color: 'white',
    fontWeight: 'bold',
    textTransform: 'none',
    padding: theme.spacing(0.75, 1.5),
    borderRadius: 8,
    fontSize: '0.875rem',
    boxShadow: theme.mode === 'dark' ? '0 2px 8px rgba(25, 118, 210, 0.3)' : '0 2px 4px rgba(0,0,0,0.1)',
    '&:hover': {
      backgroundColor: '#1565c0',
      boxShadow: theme.mode === 'dark' ? '0 4px 12px rgba(25, 118, 210, 0.4)' : '0 4px 8px rgba(0,0,0,0.15)',
    },
  },
  secondaryButton: {
    borderColor: '#1976d2',
    color: '#1976d2',
    fontWeight: 'bold',
    textTransform: 'none',
    padding: theme.spacing(0.75, 1.5),
    borderRadius: 8,
    fontSize: '0.875rem',
    backgroundColor: theme.mode === 'dark' ? 'transparent' : 'transparent',
    '&:hover': {
      borderColor: '#1565c0',
      backgroundColor: theme.mode === 'dark' ? 'rgba(25, 118, 210, 0.1)' : 'rgba(25, 118, 210, 0.08)',
    },
  },
  
  // ✅ ESTILOS DE ICONOS MEJORADOS CON TEMA DARK
  actionIcon: {
    color: theme.mode === 'dark' ? '#60a5fa' : '#1976d2',
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: theme.mode === 'dark' ? 'rgba(96, 165, 250, 0.15)' : 'rgba(25, 118, 210, 0.1)',
      transform: 'scale(1.1)',
    },
  },
  
  deleteIcon: {
    color: theme.mode === 'dark' ? '#f87171' : '#d32f2f',
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: theme.mode === 'dark' ? 'rgba(248, 113, 113, 0.15)' : 'rgba(211, 47, 47, 0.1)',
      transform: 'scale(1.1)',
    },
  },
  
  // ✅ ESTILOS DE BÚSQUEDA MEJORADOS CON TEMA DARK
  searchField: {
    '& .MuiOutlinedInput-root': {
      borderRadius: 8,
      backgroundColor: theme.mode === 'dark' ? '#374151' : '#f5f5f5',
      color: theme.palette.text.primary,
      transition: 'all 0.2s ease',
      '&:hover': {
        backgroundColor: theme.mode === 'dark' ? '#4B5563' : '#eeeeee',
      },
      '&.Mui-focused': {
        backgroundColor: theme.mode === 'dark' ? '#374151' : 'white',
        boxShadow: `0 0 0 2px ${theme.mode === 'dark' ? 'rgba(96, 165, 250, 0.3)' : 'rgba(25, 118, 210, 0.2)'}`,
      },
      '& fieldset': {
        borderColor: theme.mode === 'dark' ? '#4B5563' : '#e0e0e0',
      },
      '&:hover fieldset': {
        borderColor: theme.palette.primary.main,
      },
      '&.Mui-focused fieldset': {
        borderColor: theme.palette.primary.main,
      },
    },
    '& .MuiInputBase-input': {
      color: theme.palette.text.primary,
    },
  },
  
  // ✅ ESTILOS DE CONTADOR DE CONTACTOS
  contactsCount: {
    fontWeight: 'bold',
    color: theme.mode === 'dark' ? '#60a5fa' : '#1976d2',
    fontSize: '0.95rem',
  },
}));

const ContactLists = () => {
  const classes = useStyles();
  const theme = useTheme();
  const history = useHistory();

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [selectedContactList, setSelectedContactList] = useState(null);
  const [deletingContactList, setDeletingContactList] = useState(null);
  const [contactListModalOpen, setContactListModalOpen] = useState(false);
  const [columnAliasModalOpen, setColumnAliasModalOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [viewMode, setViewMode] = useViewMode("contactLists");
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [searchParam, setSearchParam] = useState("");
  const [contactLists, dispatch] = useReducer(reducer, []);

  const socketManager = useContext(SocketContext);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchContactLists = async () => {
        try {
          const { data } = await api.get("/contact-lists/", {
            params: { searchParam, pageNumber },
          });
          dispatch({ type: "LOAD_CONTACTLISTS", payload: data.records });
          setHasMore(data.hasMore);
          setLoading(false);
        } catch (err) {
          toastError(err);
        }
      };
      fetchContactLists();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, pageNumber]);

  useEffect(() => {
    const companyId = localStorage.getItem("companyId");
    const socket = socketManager.getSocket(companyId);

    socket.on(`company-${companyId}-ContactList`, (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_CONTACTLIST", payload: data.record });
      }

      if (data.action === "delete") {
        dispatch({ type: "DELETE_CONTACTLIST", payload: +data.id });
      }
    });

    return () => {
      socket.disconnect();
    };
  }, [socketManager]);

  const handleOpenContactListModal = () => {
    setSelectedContactList(null);
    setContactListModalOpen(true);
  };

  const handleCloseContactListModal = () => {
    setSelectedContactList(null);
    setContactListModalOpen(false);
  };

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleEditContactList = (contactList) => {
    setSelectedContactList(contactList);
    setContactListModalOpen(true);
  };

  const handleDeleteContactList = async (contactListId) => {
    try {
      await api.delete(`/contact-lists/${contactListId}`);
      toast.success(i18n.t("contactLists.toasts.deleted"));
      setDeletingContactList(null);
      setSearchParam("");
      setPageNumber(1);
    } catch (err) {
      // ✅ CORRECCIÓN: Mostrar error en modal en lugar de toast
      const errorMsg = err?.response?.data?.error || err?.response?.data?.message || err?.message || "Error al eliminar la lista de contactos";
      setErrorMessage(errorMsg);
      setErrorModalOpen(true);
      // No cerrar el modal de confirmación si hay error, para que el usuario pueda intentar de nuevo
    }
  };

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  const goToContacts = (id) => {
    history.push(`/contact-lists/${id}/contacts`);
  };

  const goToCampaigns = () => {
    history.push("/campaigns");
    setErrorModalOpen(false);
  };

  return (
    <MainContainer>
      {/* ✅ CORRECCIÓN: Modal de error para mostrar mensajes de validación del backend */}
      <Dialog
        open={errorModalOpen}
        onClose={() => setErrorModalOpen(false)}
        aria-labelledby="error-dialog-title"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="error-dialog-title" style={{ color: "#d32f2f" }}>
          <span role="img" aria-label="Advertencia">⚠️</span> No se puede eliminar la lista de contactos
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body1" style={{ whiteSpace: "pre-line" }}>
            {errorMessage}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setErrorModalOpen(false)}
            color="default"
            variant="outlined"
          >
            Cerrar
          </Button>
          <Button
            onClick={goToCampaigns}
            color="primary"
            variant="contained"
          >
            Ir a Campañas
          </Button>
        </DialogActions>
      </Dialog>
      <ConfirmationModal
        title={
          deletingContactList &&
          `${i18n.t("contactLists.confirmationModal.deleteTitle")} ${
            deletingContactList.name
          }?`
        }
        open={confirmModalOpen}
        onClose={(close) => {
          setConfirmModalOpen(close);
          if (close) {
            setDeletingContactList(null);
          }
        }}
        onConfirm={() => handleDeleteContactList(deletingContactList.id)}
      >
        {i18n.t("contactLists.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      <ContactListDialog
        open={contactListModalOpen}
        onClose={handleCloseContactListModal}
        aria-labelledby="form-dialog-title"
        contactListId={selectedContactList && selectedContactList.id}
      />
      <ColumnAliasModal
        open={columnAliasModalOpen}
        onClose={() => setColumnAliasModalOpen(false)}
      />
      <MainHeader>
        <Grid style={{ width: "99.6%" }} container spacing={2} alignItems="center">
          <Grid xs={12} sm={6} md={4} item>
            <Title>{i18n.t("contactLists.title")}</Title>
          </Grid>
          <Grid xs={12} sm={6} md={8} item>
            <Grid container spacing={1} alignItems="center" justifyContent="flex-end">
              <Grid item>
                <GuideOpenButton id="campaigns" title="Mensajería" to="/contact-lists" startId="lists" />
              </Grid>
              <Grid xs={12} sm={6} md={4} item>
                <TextField
                  fullWidth
                  placeholder={i18n.t("contacts.searchPlaceholder")}
                  type="search"
                  value={searchParam}
                  onChange={handleSearch}
                  className={classes.searchField}
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon style={{ color: theme.mode === 'dark' ? '#60a5fa' : '#1976d2' }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid xs={6} sm={3} md={2} item>
                <Button
                  fullWidth
                  variant="outlined"
                  className={classes.secondaryButton}
                  onClick={() => setColumnAliasModalOpen(true)}
                  startIcon={<SettingsIcon />}
                  size="small"
                >
                  Alias
                </Button>
              </Grid>
              <Grid xs={6} sm={3} md={3} item>
                <Button
                  fullWidth
                  variant="contained"
                  className={classes.primaryButton}
                  onClick={handleOpenContactListModal}
                  startIcon={<AddIcon />}
                  size="small"
                >
                  Nueva Lista
                </Button>
              </Grid>
              <Grid item>
                <ViewModeToggle value={viewMode} onChange={setViewMode} />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </MainHeader>
      <Paper
        className={classes.mainPaper}
        variant="outlined"
        onScroll={handleScroll}
      >
        {viewMode === "cards" ? (
          <RecordsCardsGrid>
            {contactLists.map((contactList) => (
              <RecordCard
                key={`list-card-${contactList.id}`}
                title={contactList.name}
                actions={
                  <>
                    <IconButton size="small" onClick={() => goToContacts(contactList.id)} className={classes.actionIcon}>
                      <PeopleIcon />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleEditContactList(contactList)} className={classes.actionIcon}>
                      <EditIcon />
                    </IconButton>
                    <IconButton size="small" onClick={() => { setConfirmModalOpen(true); setDeletingContactList(contactList); }} className={classes.deleteIcon}>
                      <DeleteOutlineIcon />
                    </IconButton>
                  </>
                }
              >
                <RecordCardMeta>
                  {i18n.t("contactLists.table.contacts")}: {contactList.contactsCount || 0}
                </RecordCardMeta>
              </RecordCard>
            ))}
          </RecordsCardsGrid>
        ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell align="center" className={classes.tableHeader}>
                {i18n.t("contactLists.table.name")}
              </TableCell>
              <TableCell align="center" className={classes.tableHeader}>
                {i18n.t("contactLists.table.contacts")}
              </TableCell>
              <TableCell align="center" className={classes.tableHeader}>
                {i18n.t("contactLists.table.actions")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <>
              {contactLists.map((contactList) => (
                <TableRow key={contactList.id} className={classes.tableRow}>
                  <TableCell align="center" className={classes.tableCell}>
                    {contactList.name}
                  </TableCell>
                  <TableCell align="center" className={classes.tableCell}>
                    <span className={classes.contactsCount}>
                    {contactList.contactsCount || 0}
                    </span>
                  </TableCell>
                  <TableCell align="center" className={classes.tableCell}>
                    <a href={planilhaExemplo} download="planilha.xlsx">
                      <IconButton size="small" title="Descargar Plantilla Ejemplo" className={classes.actionIcon}>
                        <DownloadIcon />
                      </IconButton>
                    </a>

                    <IconButton
                      size="small"
                      onClick={() => goToContacts(contactList.id)}
                      className={classes.actionIcon}
                      title="Ver Contactos"
                    >
                      <PeopleIcon />
                    </IconButton>

                    <IconButton
                      size="small"
                      onClick={() => handleEditContactList(contactList)}
                      className={classes.actionIcon}
                      title="Editar Lista"
                    >
                      <EditIcon />
                    </IconButton>

                    <IconButton
                      size="small"
                      onClick={(e) => {
                        setConfirmModalOpen(true);
                        setDeletingContactList(contactList);
                      }}
                      className={classes.deleteIcon}
                      title="Eliminar Lista"
                    >
                      <DeleteOutlineIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {loading && <TableRowSkeleton columns={3} />}
            </>
          </TableBody>
        </Table>
        )}
      </Paper>
    </MainContainer>
  );
};

export default ContactLists;
